import React, { useEffect, useState, useCallback } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Bell, ShieldAlert, CheckCircle2, Loader2, MessageCircle, Plus, Trash2, Settings } from 'lucide-react';
import { useAlerts, AlertRow, buildBroadcastQueue } from '@/components/Alerts';
import { toast } from 'sonner';
import { API } from '@/App';

/**
 * Always-CC recipients editor. These numbers get the WhatsApp broadcast
 * in addition to team members marked `alerts_recipient`. Use this for
 * the operator's personal number, the owner, or partner agencies that
 * aren't on the Team list. Saved to `pollen_settings.alerts.cc_phones`.
 */
const AlertsCcEditor = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const load = useCallback(async () => {
    try {
      const t = localStorage.getItem('session_token');
      const res = await fetch(`${API}/pollen/alerts/settings`, {
        credentials: 'include',
        headers: t ? { Authorization: `Bearer ${t}` } : {},
      });
      if (res.ok) {
        const data = await res.json();
        setRows(data.cc_phones || []);
      }
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const update = (i, k, v) => setRows(rs => rs.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const addRow = () => setRows(rs => [...rs, { name: '', phone: '' }]);
  const removeRow = (i) => setRows(rs => rs.filter((_, idx) => idx !== i));
  const save = async () => {
    setSaving(true);
    try {
      const t = localStorage.getItem('session_token');
      const res = await fetch(`${API}/pollen/alerts/settings`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(t ? { Authorization: `Bearer ${t}` } : {}),
        },
        body: JSON.stringify({ cc_phones: rows }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || 'Failed to save');
      }
      const data = await res.json();
      setRows(data.cc_phones || []);
      toast.success('CC numbers saved');
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };
  return (
    <Card data-testid="alerts-cc-editor">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-1.5">
          <Settings className="w-4 h-4 text-slate-500" />
          Always-CC numbers
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          These WhatsApp numbers receive the broadcast in addition to team members flagged as alert recipients.
          Use this for your own personal number, owners, or partner agencies that aren't on the Team list.
        </p>
      </CardHeader>
      <CardContent className="space-y-2">
        {loading ? (
          <div className="text-xs text-muted-foreground flex items-center gap-2">
            <Loader2 className="w-3 h-3 animate-spin" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <p className="text-xs text-muted-foreground italic py-1">No CC numbers yet.</p>
        ) : (
          rows.map((r, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`cc-row-${i}`}>
              <Input
                className="h-8 text-sm flex-1"
                placeholder="Name (e.g. Owner, You)"
                value={r.name || ''}
                onChange={(e) => update(i, 'name', e.target.value)}
                data-testid={`cc-name-${i}`}
              />
              <Input
                className="h-8 text-sm w-44"
                type="tel"
                placeholder="+91 98xxxxxxxx"
                value={r.phone || ''}
                onChange={(e) => update(i, 'phone', e.target.value)}
                data-testid={`cc-phone-${i}`}
              />
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 text-destructive"
                onClick={() => removeRow(i)}
                data-testid={`cc-remove-${i}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))
        )}
        <div className="flex items-center justify-between pt-2">
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={addRow} data-testid="cc-add">
            <Plus className="w-3 h-3 mr-1" /> Add number
          </Button>
          <Button
            size="sm"
            className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={save}
            disabled={saving}
            data-testid="cc-save"
          >
            {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
            Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const PollenDotsAlerts = () => {
  const [tab, setTab] = useState('active');
  const [sending, setSending] = useState(false);
  // Load done items when on Done tab to keep payload light otherwise.
  const { alerts, counts, done, loading, reload } = useAlerts(60_000);
  useEffect(() => {
    if (tab === 'done') reload({ includeDone: true });
  }, [tab, reload]);

  const broadcast = async () => {
    setSending(true);
    try {
      const t = localStorage.getItem('session_token');
      const res = await fetch(`${API}/pollen/alerts/broadcast`, {
        credentials: 'include',
        headers: t ? { Authorization: `Bearer ${t}` } : {},
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || 'Failed to build broadcast');
      }
      const data = await res.json();
      const recipients = data.recipients || [];
      if (recipients.length === 0) {
        toast.error('No team members marked as alerts recipients yet — open Team page and tick the "Receives daily alerts" box.');
        return;
      }
      const queue = buildBroadcastQueue(recipients, data.message);
      // Synchronous open the first window from the click handler so the
      // popup blocker treats it as a real user gesture.
      let first = true;
      for (const item of queue) {
        if (first) {
          window.open(item.url, '_blank', 'noopener');
          first = false;
        } else {
          await new Promise(r => setTimeout(r, 600));
          window.open(item.url, '_blank', 'noopener');
        }
      }
      toast.success(`WhatsApp opened for ${queue.length} recipient${queue.length === 1 ? '' : 's'}. Hit Send in each chat.`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSending(false);
    }
  };

  const grouped = (() => {
    const out = { urgent: [], warn: [], info: [] };
    alerts.forEach(a => { (out[a.severity] || out.info).push(a); });
    return out;
  })();

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-3 sm:px-6 py-6 space-y-4">
        <div className="flex items-center justify-between gap-2">
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-2" data-testid="alerts-title">
              <Bell className="w-5 h-5 text-amber-600" />
              Alerts
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Things the team needs to do today.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              size="sm"
              onClick={broadcast}
              disabled={sending || counts.total === 0}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="alerts-broadcast"
              title="Open WhatsApp for each designated team member with today's alert summary"
            >
              {sending
                ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                : <MessageCircle className="w-3.5 h-3.5 mr-1" />}
              Notify Team
            </Button>
            <div className="text-right">
              <p className="text-3xl font-semibold text-amber-700" data-testid="alerts-total">{counts.total}</p>
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">open</p>
            </div>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="grid grid-cols-3 max-w-md">
            <TabsTrigger value="active" data-testid="alerts-tab-active">
              Active ({counts.total})
            </TabsTrigger>
            <TabsTrigger value="done" data-testid="alerts-tab-done">
              Recently done
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="alerts-tab-settings">
              Settings
            </TabsTrigger>
          </TabsList>

          <TabsContent value="active" className="mt-4 space-y-4">
            {loading && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />Loading…
              </div>
            )}
            {!loading && counts.total === 0 && (
              <Card className="border-emerald-200 bg-emerald-50/60">
                <CardContent className="py-10 text-center">
                  <CheckCircle2 className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                  <p className="text-sm font-medium text-emerald-800">No active alerts.</p>
                  <p className="text-xs text-muted-foreground mt-1">Everything that needed doing is done.</p>
                </CardContent>
              </Card>
            )}
            {grouped.urgent.length > 0 && (
              <Card className="border-rose-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-1.5 text-rose-700">
                    <ShieldAlert className="w-4 h-4" />Urgent ({grouped.urgent.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {grouped.urgent.map(a => (
                    <AlertRow key={a.id} alert={a} onDone={() => reload()} />
                  ))}
                </CardContent>
              </Card>
            )}
            {grouped.warn.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-1.5 text-amber-700">
                    <Bell className="w-4 h-4" />Coming up ({grouped.warn.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {grouped.warn.map(a => (
                    <AlertRow key={a.id} alert={a} onDone={() => reload()} />
                  ))}
                </CardContent>
              </Card>
            )}
            {grouped.info.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-1.5 text-slate-600">
                    <Bell className="w-4 h-4" />Look at when you can ({grouped.info.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {grouped.info.map(a => (
                    <AlertRow key={a.id} alert={a} onDone={() => reload()} />
                  ))}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="done" className="mt-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Cleared in the last 7 days</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {(!done || done.length === 0) ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">Nothing cleared yet.</p>
                ) : (
                  done.map(a => (
                    <AlertRow key={a.id} alert={a} onReopen={() => reload({ includeDone: true })} />
                  ))
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="settings" className="mt-4 space-y-3">
            <AlertsCcEditor />
            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Team-member recipients</CardTitle>
                <p className="text-xs text-muted-foreground">
                  Open Team page and tick "Receives daily alerts broadcast" on each member you want included.
                </p>
              </CardHeader>
              <CardContent>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => window.location.assign('/pollen-dots/team')} data-testid="open-team-page">
                  Open Team page →
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default PollenDotsAlerts;
